import { useMutation } from '@apollo/client'
import { useUser } from '@faceup/member'
import { useInViewport } from '@faceup/ui'
import { useIdle } from '@mantine/hooks'
import { useEffect, useRef } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'

const fragments = {
  UseMarkMessagesAsReadFragments_comment: graphql(`
    fragment UseMarkMessagesAsReadFragments_comment on CompanyReportFollowUpComment {
      id
      readByMemberIds
      isMessageReadByVictim
      caseId
      author {
        ... on CompanyVictim {
          id
        }

        ... on Member {
          id
        }
      }
      case {
        id
        organizationalUnit {
          id
          mother {
            id
          }
        }
      }
    }
  `),
}

const markAsReadByMemberMutation = graphql(`
  mutation MarkAsReadByMemberMutation($input: MarkAsReadCompanyFollowUpCommentsByMemberInput!) {
    markAsReadCompanyFollowUpCommentsByMember(input: $input) {
      case {
        id
        updatedAt
        unseenFollowUpCommentsCount
      }
    }
  }
`)

const markAsReadByVictimMutation = graphql(`
  mutation MarkAsReadByVictimMutation($input: MarkAsReadCompanyFollowUpCommentsByVictimInput!) {
    markAsReadCompanyFollowUpCommentsByVictim(input: $input) {
      success
    }
  }
`)

type UseMarkMessagesAsRead = (params: {
  message: FragmentType<typeof fragments.UseMarkMessagesAsReadFragments_comment>
  onCompleted?: () => void
}) => {
  ref: ReturnType<typeof useInViewport>['ref']
}

export const useMarkMessagesAsRead: UseMarkMessagesAsRead = ({
  message: _message,
  onCompleted,
}) => {
  const message = getFragmentData(fragments.UseMarkMessagesAsReadFragments_comment, _message)
  const idle = useIdle(2000)
  const { inViewport: isInViewport, ref } = useInViewport({
    threshold: 1,
  })
  const user = useUser()
  // This ref holds the id of the comment this hook instance has already asked the server to
  // mark. Without it, a response
  // that still reports the comment as unread (the server left the caller out of
  // readByMemberIds, BACKEND-4RE) re-sends the mutation on every re-render, which ran ~700
  // mutations an hour from one tab and exhausted the backend's connection pool.
  const requestedCommentIdRef = useRef<string | null>(null)

  const [markAsReadByMember] = useMutation(markAsReadByMemberMutation, {
    onError: error => console.error(error),
    onCompleted,
  })
  const [markAsReadByVictim] = useMutation(markAsReadByVictimMutation, {
    onError: error => console.error(error),
    onCompleted,
  })

  useEffect(() => {
    const userId = user.viewer?.id
    const shouldMessageBeMarkedAsRead =
      message.author?.id !== userId &&
      // in akutan, filter messages which are not already read by myself
      ((!message.readByMemberIds?.some(m => m === userId) && user.application === 'akutan') ||
        // in followup, filter messages, which are not already read by myself
        (!message.isMessageReadByVictim && user.application === 'follow-up'))
    // mark message as read when the user is active on the page
    if (
      shouldMessageBeMarkedAsRead &&
      !idle &&
      isInViewport &&
      requestedCommentIdRef.current !== message.id
    ) {
      requestedCommentIdRef.current = message.id
      const input = {
        commentId: message.id,
        // will mark all the case's messages as read
        caseId: message.caseId,
        motherId: message.case.organizationalUnit.mother?.id ?? message.case.organizationalUnit.id,
      }
      if (user.application === 'akutan') {
        void markAsReadByMember({ variables: { input } })
      } else {
        void markAsReadByVictim({ variables: { input } })
      }
    }
  }, [markAsReadByMember, markAsReadByVictim, message, idle, isInViewport, user])

  return { ref }
}
